import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, CheckCircle, Clock, DollarSign, Shield, Building, Droplets, Waves, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Marquee from "react-fast-marquee";
import SEO from "@/components/SEO";
import CTABanner from "@/components/CTABanner";
import { localBusiness, GOOGLE_MAPS_URL, GOOGLE_RATING } from "@/lib/business";

export default function Home() {
  // Hide the CRMandGo floating corner bubble on the Home page only — here the chat
  // is launched from the fixed hero button instead. The bubble stays on every other page.
  useEffect(() => {
    document.body.classList.add("cag-on-home");
    return () => document.body.classList.remove("cag-on-home");
  }, []);

  // Open the CRMandGo chat by clicking its (hidden) injected button. The widget script
  // is async, so retry briefly in case the user clicks before it has finished loading.
  const openChat = () => {
    let tries = 0;
    const tryOpen = () => {
      const btn = document.querySelector(".cag-rcw-btn");
      if (btn) {
        btn.click();
        return;
      }
      if (tries++ < 25) setTimeout(tryOpen, 150);
    };
    tryOpen();
  };

  // The canonical business node. Identity (@id, address, geo, hours, sameAs,
  // image) comes from lib/business so every page describes the same entity.
  const schema = {
    "@context": "https://schema.org",
    ...localBusiness({
      description:
        "Professional building and property inspections in Darwin, NT. Pre-sale inspections, building condition reports, pool reports, and more.",
      areaServed: {
        "@type": "City",
        "name": "Darwin"
      },
      serviceType: [
        "Building Inspections",
        "Property Inspections",
        "Pre-Sale Inspections",
        "Building Condition Reports",
        "Pool Safety Inspections",
        "Plumbing Inspections"
      ]
    })
  };
  const services = [
    {
      id: "pre-sale",
      icon: Building,
      title: "Pre-Sale Property Inspections",
      description: "Get your home ready for market with confidence. Visual condition reports to help sellers prepare.",
      image: "/assets/img/house-for-sale.jpg"
    },
    {
      id: "building-status",
      icon: FileText,
      title: "Building Status Reports",
      description: "Visual and digital check on the property for all things approval related.",
      image: "/assets/img/building-status-report.jpg"
    },
    {
      id: "building-condition",
      icon: CheckCircle,
      title: "Building Condition Reports",
      description: "Visual and digital check on the condition of the building aesthetics. Includes remediation report.",
      image: "/assets/img/building-condition-report.jpg"
    },
    {
      id: "strata",
      icon: Building,
      title: "Strata Reports",
      description: "Independent reports for strata and unit properties, scoped and quoted to suit. Contact us for pricing.",
      image: "/assets/img/inspection-checklist.jpg"
    },
    {
      id: "pool",
      icon: Waves,
      title: "Pool Reports",
      description: "Ensure your pool meets Government requirements. Pass your inspection first time.",
      image: "/assets/img/pool-report.jpg"
    },
    {
      id: "plumbing",
      icon: Droplets,
      title: "Plumbing Reports",
      description: "Full visual plumbing report covering all plumbing systems and fixtures.",
      image: "/assets/img/plumbing.jpg"
    },
    {
      id: "electrical",
      icon: Shield,
      title: "Electrical Reports",
      description: "Full scope electrical report through our trusted partners.",
      image: "/assets/img/electrical.jpg"
    }
  ];

  const whatWeDo = [
    "Pre-Sale Property Inspections",
    "Visual Condition Reports (AS4349.1-aligned)",
    "72 Hour Report Delivery",
    "Digital Reports With Photos",
    "No Structural Opinions Given",
    "Fully Insured and Qualified Inspectors",
    "Book Online in Minutes",
    "Flat-Rate Pricing from $260 incl. GST",
    "Focused on Buyer Confidence",
    "Servicing Darwin and Greater NT"
  ];

  const testimonials = [
    {
      name: "Rebecca Diggins",
      location: "Darwin",
      text: "I highly recommend this company and look forward to working with them again! Professional, thorough, and the report was delivered right on time.",
      date: "2 months ago"
    },
    {
      name: "Nathan Cunneen",
      location: "Palmerston",
      text: "Aussie Inspections have been fantastic to work with. Clear communication, fair pricing, and detailed reports that made our property decisions much easier.",
      date: "3 months ago"
    },
    {
      name: "Sarah M.",
      location: "Casuarina",
      text: "Great service! The visual inspection was exactly what we needed - no confusing jargon, just straightforward findings with photos. Highly recommended.",
      date: "1 month ago"
    }
  ];

  return (
    <div>
      <SEO
        title="Darwin Building & Property Inspections"
        description="Professional building and property inspections in Darwin, NT. Pre-sale inspections, building condition reports, pool reports from $260. Fast, reliable, hassle-free service."
        keywords="building inspections Darwin, property inspections NT, pre-sale inspections Darwin, building reports Darwin, pool inspections NT, Darwin building inspector"
        url="/"
        schema={schema}
      />
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center overflow-hidden bg-slate-50">
        <div className="absolute inset-0">
          <img
            src="/assets/img/inspector-at-work.jpg"
            alt="Darwin property with pool"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-chivo text-4xl md:text-6xl font-black tracking-tight leading-tight text-slate-900 mb-6">
                Buying or selling property in Darwin?
                <span className="block text-primary mt-3">Get an independent Strata or Building & Pest Report.</span>
              </h1>
              <p className="text-xl md:text-2xl text-slate-600 leading-relaxed mb-8 font-semibold">
                Fast, reliable, and hassle-free.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  size="lg"
                  className="flex-1 basis-0 bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-8 py-6 rounded-md shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="hero-book-button"
                >
                  <Link to="/contact">Book Online Now</Link>
                </Button>
                <Button
                  type="button"
                  onClick={openChat}
                  size="lg"
                  className="flex-1 basis-0 bg-primary hover:bg-primary/90 text-white font-bold tracking-wide uppercase px-8 py-6 gap-2 rounded-md shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="hero-chat-button"
                >
                  <MessageCircle className="w-5 h-5" />
                  Chat With Our Team Now
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="mt-12 flex flex-wrap items-center gap-6">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-secondary" />
                  <span className="text-sm font-medium text-slate-700">Fully Insured</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5 text-secondary" />
                  <span className="text-sm font-medium text-slate-700">72hr Reports</span>
                </div>
                <div className="flex items-center space-x-2">
                  <DollarSign className="w-5 h-5 text-secondary" />
                  <span className="text-sm font-medium text-slate-700">From $260</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner />

      {/* Trust Strip */}
      <section className="bg-white py-8 border-y border-slate-200">
        <Marquee gradient={false} speed={40}>
          <div className="flex items-center space-x-16 px-8">
            <span className="text-slate-400 font-semibold text-sm uppercase tracking-wider opacity-50">AS4349.1 Aligned</span>
            <span className="text-slate-400 font-semibold text-sm uppercase tracking-wider opacity-50">Qualified Inspectors</span>
            <span className="text-slate-400 font-semibold text-sm uppercase tracking-wider opacity-50">Flat Rate Pricing</span>
            <span className="text-slate-400 font-semibold text-sm uppercase tracking-wider opacity-50">72 Hour Delivery</span>
            <span className="text-slate-400 font-semibold text-sm uppercase tracking-wider opacity-50">Digital Reports</span>
            <span className="text-slate-400 font-semibold text-sm uppercase tracking-wider opacity-50">Darwin Local</span>
          </div>
        </Marquee>
      </section>

      {/* What We Do Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-chivo text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              What We Do at Aussie Inspections
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Visual property inspections designed to reduce renegotiations and protect deal flow
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatWeDo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-6 rounded-lg border border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_12px_24px_rgba(30,64,175,0.15)] transition-all"
              >
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
                  <span className="text-slate-900 font-medium">{item}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-chivo text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              Our Inspection Reports
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive visual inspection reports for Darwin and Greater NT properties
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const ServiceIcon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white border border-slate-200 rounded-md hover:border-primary/50 transition-all duration-500 hover:-translate-y-1 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_12px_24px_rgba(30,64,175,0.15)] overflow-hidden"
                  data-testid={`service-card-${service.id}`}
                >
                  {service.image && (
                    <div className="relative h-48">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                    </div>
                  )}
                  <div className="p-6">
                    <ServiceIcon className="w-10 h-10 text-primary mb-4" />
                    <h3 className="font-chivo text-xl font-bold text-slate-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <Link
                      to={`/services/${service.id}`}
                      className="inline-flex items-center text-primary font-semibold hover:text-secondary transition-colors"
                      data-testid={`service-link-${service.id}`}
                    >
                      Learn More →
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-chivo text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-slate-600">
              Trusted by Darwin homeowners and agents
            </p>
            {/* Stated as attributed text, not aggregateRating markup: Google
                only permits rating schema for reviews a site collected
                itself. Plain text is what an LLM reads anyway. Update the
                count in lib/business.js — a stale one is worse than none. */}
            <p className="mt-4 text-slate-700">
              Rated{" "}
              <span className="font-semibold text-slate-900">
                {GOOGLE_RATING.value} out of 5
              </span>{" "}
              from {GOOGLE_RATING.count} Google reviews.{" "}
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-secondary hover:underline"
              >
                Read them on Google
              </a>
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg border border-slate-200"
              >
                <p className="text-slate-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-chivo font-bold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.location} • {testimonial.date}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-chivo text-4xl md:text-5xl font-bold mb-6">
              Ready to Book Your Inspection?
            </h2>
            <p className="text-xl text-blue-100 mb-4">
              Fast, hassle-free booking for agents and homeowners.
            </p>
            <p className="text-lg text-blue-100 mb-8">
              Flat-rate pricing from $260 incl. GST • 72-hour report delivery
            </p>
            <Button
              asChild
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase px-12 py-6 rounded-md shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid="cta-book-button"
            >
              <Link to="/contact">Book Now</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
